import React, { useState, useEffect } from 'react';
import { router, Head, usePage } from '@inertiajs/react';
import { Button } from '@/components/ui/button';
import { Alert, AlertTitle, AlertDescription } from '@/components/ui/alert';
import { Card, CardContent } from '@/components/ui/card';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { RefreshCw, Plug, MessageSquare, AlertTriangle, Loader2 } from 'lucide-react';
import AssignWorkersModal from '@/frontend/components/Integrations/AssignWorkersModal';
import { ConnectionStatusChip } from '@/frontend/components/Integrations/ConnectionStatusChip';
import PostConnectionModal from '@/frontend/components/PostConnectionModal';
import { usePermissions } from '@/frontend/hooks/usePermissions';
import { useJustConnectedWorker } from '@/frontend/hooks/useJustConnectedWorker';
import DashboardLayout from '@/frontend/layouts/DashboardLayout';
import AssignedWorkersBanner from '@/frontend/components/Integrations/AssignedWorkersBanner';
import { BackLink } from '@/frontend/components/BackLink';
import type { SlackIntegration, AssignWorker } from './types';

interface Props {
  page_title: string;
  slack_connected: boolean;
  slack_integration?: SlackIntegration;
  available_channels: { id: string; name: string }[];
  digital_workers: AssignWorker[];
}

const SlackPage = ({
  page_title,
  slack_connected,
  slack_integration,
  available_channels = [],
  digital_workers = [],
}: Props) => {
  const { can_manage_resources } = usePermissions();
  const [slackChannelId, setSlackChannelId] = useState(slack_integration?.channel_id || '');
  const [channelsLoaded, setChannelsLoaded] = useState(available_channels.length > 0);
  const [loadingChannels, setLoadingChannels] = useState(false);
  const { flash } = usePage<{ flash: { assign_workers?: boolean } }>().props;
  const [assignWorkersOpen, setAssignWorkersOpen] = useState(
    () => !!flash?.assign_workers && digital_workers.length > 0
  );
  const [savingWorkerAssignment, setSavingWorkerAssignment] = useState(false);
  const justConnected = useJustConnectedWorker(digital_workers);

  useEffect(() => {
    if (slack_connected && !channelsLoaded && !loadingChannels) {
      setLoadingChannels(true);
      setTimeout(() => {
        router.reload({
          only: ['available_channels'],
          onSuccess: () => {
            setChannelsLoaded(true);
            setLoadingChannels(false);
          },
          onError: () => {
            setLoadingChannels(false);
          }
        });
      }, 0);
    }
  }, [slack_connected, channelsLoaded, loadingChannels]);

  return (
    <>
      <Head title={page_title} />
      <div className="px-4 py-6">
        <div className="space-y-6 min-h-full">
          <BackLink label="Integrações" fallbackHref="/dashboard/integrations" />

          {/* Header único (padrão da ficha de credencial): quadrado do ícone +
              nome + chip de status + ações. O "brilho" gradiente, o selo de
              conectado e o título central duplicado saíram. */}
          <div className="flex items-center gap-3">
            <span className="flex size-11 shrink-0 items-center justify-center rounded-xl bg-secondary [&_svg]:size-6">
              <MessageSquare className="text-muted-foreground" />
            </span>
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2.5">
                <h1 className="text-[19px] font-semibold tracking-[-0.01em] text-foreground">Slack</h1>
                <ConnectionStatusChip connected={slack_connected} />
              </div>
            </div>
            {can_manage_resources && (
              <div className="flex shrink-0 items-center gap-2">
                <a href="/dashboard/integrations/connect_slack">
                  <Button variant="outline" size="sm">
                    {slack_connected ? <RefreshCw className="h-4 w-4" /> : <Plug className="h-4 w-4" />}
                    <span className="hidden sm:inline">{slack_connected ? 'Reconectar' : 'Conectar'}</span>
                    <span className="sm:hidden">{slack_connected ? 'Recon.' : 'Conectar'}</span>
                  </Button>
                </a>
                {slack_connected && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-muted-foreground hover:border-destructive/40 hover:text-destructive"
                    onClick={() => {
                      if (confirm('Tem certeza que deseja desconectar o Slack? Isso removerá a integração.')) {
                        router.delete('/dashboard/integrations/disconnect_slack', {
                          preserveScroll: true
                        });
                      }
                    }}
                  >
                    <span className="hidden sm:inline">Desconectar</span>
                    <span className="sm:hidden">Desc.</span>
                  </Button>
                )}
              </div>
            )}
          </div>

          {!can_manage_resources && (
            <Alert variant="warning">
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle>Você não tem permissão para gerenciar integrações.</AlertTitle>
              <AlertDescription>Você pode visualizar as integrações conectadas, mas não pode modificá-las.</AlertDescription>
            </Alert>
          )}

          <Card><CardContent className="p-6">
            {!slack_connected ? (
              <div className="space-y-4">
                <p className="text-[13px] text-muted-foreground">
                  Conecte sua conta do Slack para receber notificações em tempo real.
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="bg-muted rounded-lg p-4">
                  <h3 className="text-[13px] font-semibold mb-2">Workspace conectado</h3>
                  <div className="flex items-center gap-3">
                    <div>
                      <p className="text-[13px] font-medium text-foreground">
                        {slack_integration?.team_name || 'Workspace'}
                      </p>
                      <p className="text-[11.5px] text-muted-foreground">
                        Conectado em {slack_integration?.connected_at ? new Date(slack_integration.connected_at).toLocaleDateString('pt-BR') : 'N/A'}
                      </p>
                      <p className="text-[11.5px] text-muted-foreground">
                        Por: {slack_integration?.connected_by || 'N/A'}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-muted rounded-lg p-4">
                  <h3 className="text-[13px] font-semibold mb-2">Configuração do canal</h3>
                  <p className="text-[11.5px] text-muted-foreground mb-3">
                    Selecione o canal onde as notificações serão enviadas.
                  </p>
                  <div className="flex gap-2 items-end">
                    {loadingChannels ? (
                      <div className="flex-1 px-3 py-2 bg-accent border border-border rounded-md text-muted-foreground flex items-center gap-2">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        <p className="text-[11.5px] text-muted-foreground">Carregando canais…</p>
                      </div>
                    ) : (
                      <div className="flex-1 grid gap-2">
                        <Label>Canal de notificações</Label>
                        <Select value={slackChannelId} onValueChange={(value) => setSlackChannelId(value)} disabled={!can_manage_resources}>
                          <SelectTrigger>
                            <SelectValue placeholder="Selecione um canal" />
                          </SelectTrigger>
                          <SelectContent>
                            {available_channels.map((channel) => (
                              <SelectItem key={channel.id} value={channel.id}>#{channel.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={!can_manage_resources || !slackChannelId}
                      onClick={() => {
                        if (!can_manage_resources) return;
                        router.patch('/dashboard/integrations/update_slack_channel', {
                          channel_id: slackChannelId
                        }, {
                          preserveScroll: true,
                        });
                      }}
                    >
                      Salvar
                    </Button>
                  </div>
                  {slack_integration?.channel_id && (
                    <p className="text-[11.5px] text-muted-foreground mt-2">
                      Canal atual: {slack_integration.channel_id}
                    </p>
                  )}
                </div>
              </div>
            )}
          </CardContent></Card>

          <AssignedWorkersBanner
            workers={digital_workers.filter((w) => w.assigned)}
            onAddWorker={() => setAssignWorkersOpen(true)}
            onRemoveWorker={(workerId) => router.delete('/dashboard/integrations/remove_worker', { data: { provider: 'slack', worker_id: workerId }, preserveScroll: true })}
          />
        </div>
      </div>

      <AssignWorkersModal
        open={assignWorkersOpen}
        onClose={() => setAssignWorkersOpen(false)}
        providerLabel="Slack"
        workers={digital_workers}
        provider="slack"
        saving={savingWorkerAssignment}
        onSavingChange={setSavingWorkerAssignment}
      />

      {justConnected.worker && (
        <PostConnectionModal
          open={justConnected.open}
          onClose={justConnected.close}
          worker={justConnected.worker}
          context="Slack"
        />
      )}
    </>
  );
};

SlackPage.layout = (page: React.ReactElement) => <DashboardLayout>{page}</DashboardLayout>;

export default SlackPage;
